import { useFormik } from "formik";
import { useEffect, useState } from "react";
import {
  Button,
  CardList,
  Dropzone,
  StepNextButton,
} from "../../../components";
import {
  CardVariantType,
  DisplayVariant,
} from "../../../components/CardList/CardList";
import { useLoanApplication } from "../../../contexts";
import { MediaPurpose, MediaType } from "../../../hooks/api/types";
import {
  CustomFile,
  GENERIC_DOCUMENTS_UPLOAD_FORM_VALIDATION_SCHEMA,
  GenericDocumentsUploadFormFields,
} from "./service";
import { formatBytes } from "../../../utils";
import { useDeleteMedia, useMedia } from "../../../hooks";
import { getMediaFromStatementIds } from "../../LoanCalculator/service";
import { getFileIcon } from "../../../utils/fileIcon.util";
import {
  getDropzoneAcceptForPurpose,
  getExtensionsHintForPurpose,
} from "../../../utils/accept.util";

interface GenericDocumentUploadProps {
  closeModal: () => void;
}

//todo: create a universal utility like this in commons supporting all types
//a function that take CustomFile and return MediaType based on the extension
const getMediaType = (file: CustomFile): MediaType => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  const imageExtensions = ["png", "jpg", "jpeg", "gif", "heic", "pjpeg"];
  const documentExtensions = [
    "pdf",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "txt",
  ];

  if (imageExtensions.includes(extension ?? "")) return MediaType.IMAGE;
  if (documentExtensions.includes(extension ?? "")) return MediaType.DOCUMENT;

  throw new Error(
    `Unsupported file extension "${extension}" for file "${file.name}"`
  );
};

const GenericDocumentUpload: React.FC<GenericDocumentUploadProps> = ({
  closeModal,
}) => {
  const [genericDocuments, setGenericDocuments] = useState<CustomFile[]>([]);
  const [genericDocumentsIds, setGenericDocumentsIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean[]>([]);
  const [isFailedUploadExist, setIsFailedUploadExist] =
    useState<boolean>(false);

  const {
    state,
    actions: { update },
  } = useLoanApplication();

  const { upload, get } = useMedia();
  const { mutateAsync: deleteMediaById } = useDeleteMedia();

  useEffect(() => {
    if (state.docGeneric.length && !state.docGenericFiles.length) {
      getMediaFromStatementIds(genericDocumentsIds, get, (retrievedMedia) => {
        setGenericDocuments([...retrievedMedia]);
        update({
          docGenericFiles: [...retrievedMedia],
        });
      });
    } else if (state.docGenericFiles.length)
      setGenericDocuments(state.docGenericFiles);
  }, [genericDocumentsIds]);

  useEffect(() => {
    let isUploadError = false;
    genericDocuments.forEach((genericDocument) => {
      if (genericDocument.error) isUploadError = true;
    });
    if (!isUploadError) setIsFailedUploadExist(false);
  }, [isFailedUploadExist, genericDocuments]);

  const { errors, touched, values, handleSubmit, validateForm, setErrors } =
    useFormik<GenericDocumentsUploadFormFields>({
      initialValues: {
        genericDocuments: [],
      },
      validationSchema: GENERIC_DOCUMENTS_UPLOAD_FORM_VALIDATION_SCHEMA,
      onSubmit: async () => {
        if (!values.genericDocuments.length) {
          closeModal();
          return;
        }

        setUploading((prev) => {
          let next = [...prev];
          values.genericDocuments.forEach((item, index) => {
            next[index] = true;
          });
          return next;
        });
        setLoading(true);

        const genericDocumentPromises = values.genericDocuments.map((file) =>
          upload({
            file,
            type: getMediaType(file),
            purpose: MediaPurpose.GENERIC_DOCUMENT,
            loanApplicationId: state.id,
          }).catch((err) => {
            file.error = "Upload failed";
            setIsFailedUploadExist(true);
            setLoading(false);
          })
        );

        const newGenericDocumentIds = (
          await Promise.all(genericDocumentPromises)
        ).filter(Boolean) as string[];
        setGenericDocuments((prev) => {
          const next = prev.map((statement: any, index: number) => {
            if (!statement.id) {
              statement.id = newGenericDocumentIds[index];
            }
            return statement as CustomFile;
          });
          return next;
        });
        setUploading((prev) => {
          let next = [...prev];
          next = next.map(() => false);
          return next;
        });

        update({
          docGenericFiles: getFilesToUpdate(),
          docGeneric: [...state.docGeneric, ...newGenericDocumentIds],
        })
          .then(() => {
            setLoading(false);
            if (!isFailedUploadExist) closeModal();
          })
          .catch((err) => {
            setLoading(false);
            console.log(err);
          });
      },
    });

  useEffect(() => {
    if (state.docGeneric) {
      setGenericDocumentsIds(state.docGeneric);
    }
  }, [state.docGeneric]);

  const getFilesToUpdate = () =>
    state.docGeneric.length
      ? [...state.docGenericFiles, ...values.genericDocuments]
      : [...genericDocuments];

  const handleUplaod = async (files: File[]) => {
    setErrors({});
    if (!files) return;

    const updatedGenericDocuments = [...values.genericDocuments];

    files.forEach((file: File) => {
      let allFilesUploaded = values.genericDocuments.concat(genericDocuments);
      let isFileAlreadyAdded = allFilesUploaded.some(
        (existingFile) => existingFile.name === file.name
      );
      if (!isFileAlreadyAdded) {
        setUploading((prev) => {
          const next = [...prev];
          next.push(false);
          return next;
        });
        updatedGenericDocuments.push(new CustomFile(file));
      }
    });
    values.genericDocuments = [...updatedGenericDocuments];
    state.docGenericFiles.length
      ? setGenericDocuments((prev) => [...updatedGenericDocuments, ...prev])
      : setGenericDocuments(updatedGenericDocuments);
  };

  const handleFileClose = async (fileName: string, id?: string) => {
    if (id) {
      deleteMediaById(id)
        .then((res) => {
          setGenericDocuments((prev) =>
            prev.filter(
              (genericDocument: CustomFile) => genericDocument.id !== id
            )
          );
          update({
            docGenericFiles: state.docGenericFiles.filter(
              (genericDocument) => genericDocument.id !== id
            ),
            docGeneric: state.docGeneric.filter(
              (docGenericId) => docGenericId !== id
            ),
          });
        })
        .catch((err) => {
          console.log(err);
        });
    } else if (fileName) {
      values.genericDocuments = values.genericDocuments.filter(
        (statement) => fileName !== statement.name
      );
      setGenericDocuments((prev) => {
        const next = prev.filter(
          (genericDocument) => fileName !== genericDocument.name
        );
        return next;
      });
      values.genericDocuments = values.genericDocuments.filter(
        (genericDocument) => fileName !== genericDocument.name
      );
      validateForm();
    }
  };

  const handleRetryUpload = (fileName: string) => {
    const file = values.genericDocuments.find(
      (genericDocument: File) => genericDocument.name === fileName
    );
    if (file) {
      let documentIndex: number;
      genericDocuments.forEach((genericDocument, index) => {
        if (genericDocument.name === fileName) documentIndex = index;
      });
      setUploading((prev) => {
        let next = [...prev];
        next = next.map((item, index) => {
          if (documentIndex === index) return true;
          else return false;
        });
        return next;
      });
      upload({
        file,
        type: getMediaType(file),
        purpose: MediaPurpose.GENERIC_DOCUMENT,
        loanApplicationId: state.id,
      })
        .then((res: string) => {
          setUploading((prev) => prev.map(() => false));
          values.genericDocuments = values.genericDocuments.filter(
            (genericDocument) => genericDocument.name !== fileName
          );
          setGenericDocuments((prev) => {
            let next = [...prev];
            next = next.map((genericDocument, index) => {
              if (documentIndex === index) {
                genericDocument.error = "";
                genericDocument.id = res;
                return genericDocument;
              } else {
                return genericDocument;
              }
            });
            return next;
          });
          update({
            docGenericFiles: [...state.docGenericFiles, file],
          });
        })
        .catch((err) => {
          setUploading((prev) => prev.map(() => false));
        });
    }
  };
  return (
    <div className="cx-flex cx-flex-col cx-items-center">
      <div className="cx-text-text-primary cx-font-bold cx-text-3xl cx-text-center cx-mb-2">
        Upload Additional Financial Documents
      </div>
      <div className="cx-text-text-secondary cx-text-center cx-mb-10">
        You can add audit reports or other financial documents to support your
        application.
      </div>
      <div className="cx-w-full cx-max-w-[400px]">
        <div className="cx-mb-10">
          <Dropzone
            variant="simple"
            extensions={getExtensionsHintForPurpose(
              MediaPurpose.GENERIC_DOCUMENT,
              50
            )}
            options={{
              accept: getDropzoneAcceptForPurpose(
                MediaPurpose.GENERIC_DOCUMENT
              ),
              maxSize: 50 * 1024 * 1024,
            }}
            onDrop={handleUplaod}
          />
        </div>
        <form onSubmit={handleSubmit}>
          {genericDocuments.map((genericDocument: CustomFile, index) => {
            return (
              <div key={index} className="cx-mb-2">
                <CardList
                  variant={
                    genericDocument.error
                      ? CardVariantType.UPLOAD_FAILED
                      : CardVariantType.UPLOADED
                  }
                  title={genericDocument.name}
                  description={
                    genericDocument.error
                      ? genericDocument.error
                      : touched.genericDocuments &&
                        values.genericDocuments.length &&
                        !!errors.genericDocuments?.[index]
                      ? (errors.genericDocuments[index] as string)
                      : formatBytes(genericDocument.size, 2)
                  }
                  fullWidth
                  icon={getFileIcon(genericDocument)}
                  handleFileClose={handleFileClose}
                  handleRetryUpload={handleRetryUpload}
                  error={
                    genericDocument.error
                      ? true
                      : touched.genericDocuments &&
                        !!errors.genericDocuments?.[index] &&
                        !!values.genericDocuments.length
                  }
                  uploading={uploading[index]}
                  documentId={genericDocument.id}
                  truncateTitleLength={35}
                  displayVariant={DisplayVariant.NEW}
                />
              </div>
            );
          })}
          <div className="cx-w-full cx-flex cx-flex-col cx-gap-x-4 cx-mt-10">
            <StepNextButton
              label={"Submit"}
              type={"submit"}
              disabled={isFailedUploadExist}
              loading={loading}
            />
            <Button label={"Cancel"} fullWidth secondary onClick={closeModal} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenericDocumentUpload;
